import { parseData } from '@devhacker/shared/utils/parseData';
import { fetchFavorites, type FavoritesParams } from '@devhacker/shared/features/profile/api';
import { getAccessToken, getSubId } from '@devhacker/shared/features/auth/utils/token';

import { GetArticleViewsCountSchema } from '~/schemas/views';
import { FetchCommentsCountSchema } from '~/schemas/talker';
import { defineStore } from 'pinia';
import { wrapPiniaStore } from '@devhacker/shared/utils/wrapPiniaStore';

/** Страница закладок в форме тела ответа profile `GET /users/{id}/favorites`. */
export type FavoritesPage = {
  data: Array<IFavoriteItem>;
  meta: {
    total: number;
    current_page: number;
    per_page: number;
  };
};

export interface ArticlesState {
  userRelated: {
    favorites: Array<IFavoriteItem>;
  };
  articleTitle: string;
  commentsCount: {
    [propName: string]: number;
  };
  viewsCount: {
    [propName: string]: number;
  };
}

export const useArticlesStore = wrapPiniaStore(
  defineStore('articles', {
    state: (): ArticlesState => ({
      userRelated: {
        favorites: [] as IFavoriteItem[],
      },
      articleTitle: '',
      commentsCount: {} as Record<string, number>,
      viewsCount: {} as Record<string, number>,
    }),

    getters: {
      getCommentsCount: (state) => (postId: number) => {
        return state.commentsCount[String(postId)] || 0;
      },

      currentArticleTitle: (state) => () => {
        return state.articleTitle;
      },

      getViewsCount: (state) => (postId: number) => {
        return Number(state.viewsCount[String(postId)]) || 0;
      },

      getArticleFavoriteId: (state) => (postId: number) => {
        const found = state.userRelated.favorites.find((f: IFavoriteItem) => f.post_id === postId);
        return found ? found.id : 0;
      },

      getArticleFavorite: (state) => (postId: number) => {
        return state.userRelated.favorites.find((f: IFavoriteItem) => f.post_id === postId) || null;
      },
    },

    actions: {
      async fetchAndSetFavorites(
        params: FavoritesParams,
        isUpdating = true,
      ): Promise<FavoritesPage | undefined> {
        try {
          const token = getAccessToken();
          if (!token) return;

          const subId = getSubId(token);
          if (!subId) return;

          const resp = await fetchFavorites<FavoritesPage>(subId, params);

          const favorites = resp.data;
          const newValue = isUpdating
            ? favorites
            : [...favorites, ...this.userRelated.favorites].filter(this.onlyUnique);
          this.updateFavorites(newValue);

          return resp;
        } catch (error) {
          console.error('fetchAndSetFavorites', error);
        }
      },

      setCurrentTitle(payload: string) {
        this.updateCurrentTitle(payload);
      },

      async fetchCommentsCount(payload: number[]) {
        try {
          const resp = await useNuxtApp().$talkerApi.fetchCommentsCount(payload);
          const parsedResponse = parseData(resp.data, FetchCommentsCountSchema);

          if (resp.status === 200) {
            this.updateCommentCounts(parsedResponse.data);
          }
        } catch (error) {
          console.error('fetchCommentsCount', error);
        }
      },

      async fetchViewsCount(payload: number[]) {
        try {
          const resp = await useNuxtApp().$viewsApi.getArticleViewsCount(payload);

          if (resp.status === 200) {
            const data = parseData(resp.data, GetArticleViewsCountSchema);
            this.updateViewsCounts(data.viewsCounts);
          }
        } catch (error) {
          console.error('fetchViewsCount', error);
        }
      },

      onlyUnique(value: any, index: number, self: Array<any>) {
        return self.findIndex((item) => item.id === value.id) === index;
      },

      updateFavorites(favorites: Array<IFavoriteItem>) {
        // TODO: Check if reactivity working without vue set
        this.userRelated.favorites = [...favorites, ...this.userRelated.favorites].filter(
          this.onlyUnique,
        );
      },

      updateCurrentTitle(title: string) {
        this.articleTitle = title;
      },

      addFavorite(favorite: IFavoriteItem) {
        this.userRelated.favorites.push(favorite);
      },

      removeFavorite(postId: number) {
        this.userRelated.favorites = this.userRelated.favorites.filter(
          (favorite) => favorite.post_id !== postId,
        );
      },

      updateCommentCounts(commentCounts: { post_ID: number; count: number }[]) {
        commentCounts.forEach((item) => {
          // TODO: Check if reactivity working without vue set
          this.commentsCount[item.post_ID] = item.count;
        });
      },

      clearUserRelatedData() {
        this.userRelated = { favorites: [] };
      },

      updateViewsCounts(viewsCounts: { [propName: string]: number }) {
        // TODO: Check if reactivity working without vue set
        this.viewsCount = { ...this.viewsCount, ...viewsCounts };
      },
    },
  }),
);
